import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, MapPin, Calendar, Car, Trash2, BellRing, AlertTriangle, Truck, Upload, ArrowRight } from "lucide-react";

const statusColor = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-primary/10 text-primary border-primary/30",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

const VEHICLE_TYPES = ["two_wheeler", "three_wheeler", "four_wheeler", "commercial"];
const FUELS = ["petrol", "diesel", "cng", "lpg", "ev", "hybrid"];

function daysUntil(iso) {
  if (!iso) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const then = new Date(iso); then.setHours(0, 0, 0, 0);
  return Math.round((then - now) / 86400000);
}

export default function CustomerDashboard() {
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [commercial, setCommercial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkType, setBulkType] = useState("commercial");
  const [bulkFuel, setBulkFuel] = useState("diesel");
  const [vForm, setVForm] = useState({ vehicle_number: "", vehicle_type: "", fuel_type: "", nickname: "", cert_valid_until: "" });
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = async () => {
    try {
      const [b, v, c] = await Promise.all([
        api.get("/bookings/mine"),
        api.get("/vehicles"),
        api.get("/commercial/profile").catch(() => ({ data: null })),
      ]);
      setBookings(b.data);
      setVehicles(v.data);
      setCommercial(c.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addVehicle = async () => {
    if (!vForm.vehicle_number || !vForm.vehicle_type || !vForm.fuel_type) { toast.error("Fill all required fields"); return; }
    try {
      await api.post("/vehicles", vForm);
      toast.success("Vehicle saved");
      setVehicleDialogOpen(false);
      setVForm({ vehicle_number: "", vehicle_type: "", fuel_type: "", nickname: "", cert_valid_until: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const bulkAdd = async () => {
    const rows = bulkText.split(/[\n,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
    if (rows.length === 0) { toast.error("Enter at least one vehicle number"); return; }
    try {
      const res = await api.post("/vehicles/bulk", {
        vehicles: rows.map((n) => ({ vehicle_number: n, vehicle_type: bulkType, fuel_type: bulkFuel })),
      });
      toast.success(`${res.data.added.length} added${res.data.skipped.length ? `, ${res.data.skipped.length} already existed` : ""}`);
      setBulkOpen(false);
      setBulkText("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const deleteVehicle = async (id) => {
    try {
      await api.delete(`/vehicles/${id}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const bookVehicle = (v) => {
    const params = new URLSearchParams({
      vehicle: v.vehicle_number,
      type: v.vehicle_type || "",
      fuel: v.fuel_type || "",
    });
    navigate(`/book?${params.toString()}`);
  };

  // Aggregate latest expiry from bookings into a lookup
  const latestExpiryByVehicle = React.useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.cert_valid_until) continue;
      const key = (b.vehicle_number || "").toUpperCase();
      if (!map[key] || map[key] < b.cert_valid_until) map[key] = b.cert_valid_until;
    }
    return map;
  }, [bookings]);

  const upcomingReminders = vehicles
    .map((v) => ({ ...v, cert_valid_until: v.cert_valid_until || latestExpiryByVehicle[v.vehicle_number] }))
    .filter((v) => v.cert_valid_until)
    .map((v) => ({ ...v, days: daysUntil(v.cert_valid_until) }))
    .filter((v) => v.days !== null && v.days <= 30)
    .sort((a, b) => a.days - b.days);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2 flex items-center gap-2">
              {commercial ? <><Truck className="w-3 h-3" /> Commercial account</> : "Customer dashboard"}
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">{commercial?.company_name || "My PUC Bookings"}</h1>
            {commercial && <div className="text-sm text-muted-foreground mt-2">{commercial.business_type?.replace("_", " ")} · Fleet size {commercial.fleet_size} · {commercial.city}, {commercial.state}</div>}
          </div>
          <div className="flex gap-3">
            {!commercial && (
              <Link to="/register/commercial" data-testid="upgrade-commercial-btn">
                <Button variant="outline" className="rounded-lg border-border text-foreground hover:bg-secondary h-12">
                  <Truck className="w-4 h-4 mr-2" /> Upgrade to commercial
                </Button>
              </Link>
            )}
            <Button data-testid="new-booking-btn" onClick={() => navigate("/partners")} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 px-6">
              <Plus className="w-4 h-4 mr-2" /> Book new test
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : (
          <>
            {upcomingReminders.length > 0 && (
              <div className="mb-10 border border-primary/30 bg-primary/10 p-5 flex items-start gap-3" data-testid="upcoming-reminders">
                <BellRing className="w-5 h-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <div className="font-display text-lg font-bold tracking-tight mb-1">Upcoming PUC renewals</div>
                  <ul className="text-sm space-y-1">
                    {upcomingReminders.map((v) => (
                      <li key={v.vehicle_id || v.vehicle_number} className="flex items-center justify-between gap-3">
                        <span><span className="font-mono">{v.vehicle_number}</span> expires <span className="text-primary">{v.cert_valid_until}</span></span>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs uppercase tracking-[0.15em] ${v.days <= 3 ? "text-destructive" : "text-muted-foreground"}`}>
                            {v.days <= 0 ? "Expired" : `${v.days}d left`}
                            {v.days <= 3 && v.days > 0 && <AlertTriangle className="inline w-3 h-3 ml-1" />}
                          </span>
                          <Button data-testid={`reminder-book-${v.vehicle_id || v.vehicle_number}`} size="sm" onClick={() => bookVehicle(v)} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-7 text-xs">
                            Book
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* My Vehicles */}
            <section className="mb-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl font-bold tracking-tight">My Vehicles</h2>
                <div className="flex gap-2">
                  {commercial && (
                    <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="bulk-add-btn" variant="outline" className="rounded-lg border-border text-foreground hover:bg-secondary h-10">
                          <Upload className="w-4 h-4 mr-2" /> Bulk add fleet
                        </Button>
                      </DialogTrigger>
                      <DialogContent data-testid="bulk-add-dialog" className="rounded-lg border-border bg-card">
                        <DialogHeader><DialogTitle className="font-display tracking-tight">Bulk-add fleet vehicles</DialogTitle></DialogHeader>
                        <p className="text-sm text-muted-foreground">Paste vehicle numbers separated by commas or new lines. All will be tagged with the class and fuel type below.</p>
                        <div>
                          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle numbers</Label>
                          <Textarea data-testid="bulk-numbers" value={bulkText} onChange={(e) => setBulkText(e.target.value)} className="rounded-lg bg-secondary border-border mt-2 font-mono min-h-[140px]" placeholder={"MH12AB1234\nDL01CD5678\nKA05EF9012"} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Class</Label>
                            <Select value={bulkType} onValueChange={setBulkType}>
                              <SelectTrigger data-testid="bulk-type" className="rounded-lg bg-secondary border-border h-11 mt-2"><SelectValue /></SelectTrigger>
                              <SelectContent className="rounded-lg">
                                {VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Fuel</Label>
                            <Select value={bulkFuel} onValueChange={setBulkFuel}>
                              <SelectTrigger data-testid="bulk-fuel" className="rounded-lg bg-secondary border-border h-11 mt-2"><SelectValue /></SelectTrigger>
                              <SelectContent className="rounded-lg">
                                {FUELS.map((f) => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button data-testid="bulk-save" onClick={bulkAdd} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Add fleet</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-vehicle-btn" variant="outline" className="rounded-lg border-border text-foreground hover:bg-secondary h-10">
                        <Plus className="w-4 h-4 mr-2" /> Add vehicle
                      </Button>
                    </DialogTrigger>
                  <DialogContent data-testid="add-vehicle-dialog" className="rounded-lg border-border bg-card">
                    <DialogHeader>
                      <DialogTitle className="font-display tracking-tight">Add a vehicle</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle number</Label>
                        <Input data-testid="vehicle-number" value={vForm.vehicle_number} onChange={(e) => setVForm({ ...vForm, vehicle_number: e.target.value })} className="rounded-lg bg-secondary border-border h-11 mt-2 font-mono uppercase" placeholder="MH12AB1234" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle class</Label>
                        <Select value={vForm.vehicle_type} onValueChange={(v) => setVForm({ ...vForm, vehicle_type: v })}>
                          <SelectTrigger data-testid="vehicle-type" className="rounded-lg bg-secondary border-border h-11 mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent className="rounded-lg">
                            {VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Fuel type</Label>
                        <Select value={vForm.fuel_type} onValueChange={(v) => setVForm({ ...vForm, fuel_type: v })}>
                          <SelectTrigger data-testid="vehicle-fuel" className="rounded-lg bg-secondary border-border h-11 mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent className="rounded-lg">
                            {FUELS.map((f) => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Nickname (optional)</Label>
                        <Input data-testid="vehicle-nickname" value={vForm.nickname} onChange={(e) => setVForm({ ...vForm, nickname: e.target.value })} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="Family car" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Current PUC valid until (optional)</Label>
                        <Input data-testid="vehicle-cert-valid" type="date" value={vForm.cert_valid_until} onChange={(e) => setVForm({ ...vForm, cert_valid_until: e.target.value })} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                        <p className="text-[11px] text-muted-foreground mt-1.5">We'll remind you 3 days before this date so your PUC never lapses.</p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button data-testid="vehicle-save" onClick={addVehicle} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Save vehicle</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                </div>
              </div>

              {vehicles.length === 0 ? (
                <div className="border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="empty-vehicles">
                  No saved vehicles yet. Save one and we&apos;ll remind you 3 days before every PUC expiry.
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border" data-testid="vehicles-grid">
                  {vehicles.map((v) => {
                    const validity = v.cert_valid_until || latestExpiryByVehicle[v.vehicle_number];
                    const d = daysUntil(validity);
                    // Colour chip logic
                    let chip = null;
                    if (!validity) {
                      chip = { label: "No PUC on file", cls: "bg-muted text-muted-foreground border-border" };
                    } else if (d <= 0) {
                      chip = { label: `Expired ${Math.abs(d)}d ago`, cls: "bg-red-100 text-red-800 border-red-300" };
                    } else if (d <= 7) {
                      chip = { label: `Expires in ${d}d`, cls: "bg-orange-100 text-orange-800 border-orange-300" };
                    } else if (d <= 30) {
                      chip = { label: `${d} days left`, cls: "bg-amber-100 text-amber-800 border-amber-300" };
                    } else {
                      chip = { label: `Valid · ${d}d`, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
                    }
                    return (
                      <div key={v.vehicle_id} className="bg-card p-5 flex flex-col" data-testid={`vehicle-${v.vehicle_id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="font-mono text-lg">{v.vehicle_number}</div>
                          <button onClick={() => deleteVehicle(v.vehicle_id)} data-testid={`delete-vehicle-${v.vehicle_id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <span
                          data-testid={`vehicle-status-chip-${v.vehicle_id}`}
                          className={`inline-flex self-start items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] ${chip.cls}`}
                        >
                          {chip.label}
                        </span>
                        <div className="text-xs text-muted-foreground mt-3">{v.vehicle_type?.replace("_", " ")} · {v.fuel_type?.toUpperCase()}</div>
                        {v.nickname && <div className="text-xs text-muted-foreground mt-1">{v.nickname}</div>}
                        {validity ? (
                          <div className="mt-4 pt-4 border-t border-border">
                            <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">PUC valid till</div>
                            <div className="font-medium">{validity}</div>
                          </div>
                        ) : (
                          <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">No active certificate yet.</div>
                        )}
                        <Button
                          data-testid={`book-vehicle-${v.vehicle_id}`}
                          onClick={() => bookVehicle(v)}
                          className="mt-4 w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-10"
                        >
                          Book now <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Bookings */}
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">Bookings</h2>
            {bookings.length === 0 ? (
              <div className="border border-dashed border-border p-16 text-center" data-testid="empty-bookings">
                <Car className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-display text-2xl font-bold mb-2">No bookings yet</h3>
                <p className="text-muted-foreground mb-6">Start by finding a certified PUC centre near you.</p>
                <Link to="/partners">
                  <Button className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Browse centres</Button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-4" data-testid="customer-bookings-list">
                {bookings.map((b) => (
                  <Card key={b.booking_id} data-testid={`booking-${b.booking_id}`} className="rounded-lg border-border bg-card p-6">
                    <div className="flex flex-wrap items-start justify-between gap-6">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className={`rounded-lg border ${statusColor[b.status]}`}>{b.status.toUpperCase()}</Badge>
                          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="font-display text-2xl font-bold tracking-tight">{b.partner_snapshot?.store_name}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1"><MapPin className="w-3.5 h-3.5" /> {b.partner_snapshot?.city} — {b.partner_snapshot?.pincode}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-6 text-sm">
                        <div>
                          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Vehicle</div>
                          <div className="font-mono">{b.vehicle_number}</div>
                          <div className="text-muted-foreground text-xs mt-1">{b.vehicle_type.replace("_", " ")} · {b.fuel_type}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Slot</div>
                          <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> {b.slot_date}</div>
                          <div className="text-muted-foreground text-xs mt-1">{b.slot_time}</div>
                          {b.cert_valid_until && <div className="text-xs text-primary mt-1">PUC valid till {b.cert_valid_until}</div>}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
